import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { PLATFORM_COLORS } from "@/components/analytics/platform-breakdown-chart";
import type { AnalyticsGranularity, AnalyticsSharePoint } from "@/types";

const numberFormatter = new Intl.NumberFormat();

interface TooltipPayloadEntry {
  dataKey: string;
  name: string;
  value: number;
  color: string;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadEntry[];
  label?: string;
}

function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload?.length) {
    return null;
  }

  // Sort payload by value in descending order
  const sortedPayload = [...payload].sort((a, b) => b.value - a.value);

  return (
    <div
      style={{
        backgroundColor: "rgb(255 255 255)",
        border: "1px solid rgb(229 229 229)",
        borderRadius: "8px",
        padding: "12px",
        boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
      }}
    >
      <p
        style={{
          color: "rgb(23 23 23)",
          fontWeight: 600,
          marginBottom: "8px",
        }}
      >
        {label}
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
        {sortedPayload.map((entry) => (
          <div
            key={entry.dataKey}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <span
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor: entry.color,
                flexShrink: 0,
              }}
            />
            <span
              style={{
                color: "rgb(64 64 64)",
                fontSize: "14px",
              }}
            >
              {entry.name}: {numberFormatter.format(entry.value)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

const PLATFORM_LABELS: Record<string, string> = {
  facebook: "Facebook",
  twitter: "X (Twitter)",
  linkedin: "LinkedIn",
  pinterest: "Pinterest",
  whatsapp: "WhatsApp",
  slack: "Slack",
  instagram: "Instagram",
  reddit: "Reddit",
  discord: "Discord",
  telegram: "Telegram",
  snapchat: "Snapchat",
  tiktok: "TikTok",
  wechat: "WeChat",
  tumblr: "Tumblr",
  mastodon: "Mastodon",
  signal: "Signal",
  youtube: "YouTube",
  other: "Other",
  direct: "Direct",
};

interface SharesOverTimeChartProps {
  data: AnalyticsSharePoint[];
  granularity: AnalyticsGranularity;
}

export function SharesOverTimeChart({ data, granularity }: SharesOverTimeChartProps) {
  if (!data.length) {
    return (
      <div className="text-muted-foreground flex h-[280px] items-center justify-center text-sm">
        No share data available for this range yet
      </div>
    );
  }

  // Extract all unique platforms from the data
  const allPlatforms = new Set<string>();
  data.forEach((point) => {
    if (point.platforms) {
      const platforms = point.platforms;
      Object.keys(platforms).forEach((platform: string) => {
        allPlatforms.add(platform);
      });
    }
  });

  // Sort platforms by total count (descending) for better visual stacking
  const platformTotals = Array.from(allPlatforms).map((platform) => {
    const total = data.reduce((sum, point) => {
      if (point.platforms) {
        const platforms = point.platforms;
        const count: number = platforms[platform] ?? 0;
        return sum + count;
      }
      return sum;
    }, 0);
    return { platform, total };
  });
  platformTotals.sort((a, b) => b.total - a.total);
  const sortedPlatforms = platformTotals.map((p) => p.platform);

  // Transform data to include platform counts as individual properties
  // Ensure all platforms have a value (0 if not present) for every data point
  const chartData = data.map((point) => {
    const platformData: Record<string, number> = {};

    // Initialize all platforms with 0
    sortedPlatforms.forEach((platform) => {
      platformData[platform] = 0;
    });

    // Fill in actual values
    if (point.platforms) {
      const platforms = point.platforms;
      Object.entries(platforms).forEach(([platform, count]: [string, number]) => {
        platformData[platform] = count;
      });
    }

    return {
      label: point.label,
      date: point.date,
      ...platformData,
    };
  });

  const formatAxisTick = (value: number | string) => numberFormatter.format(Number(value));

  return (
    <ResponsiveContainer width="100%" height={280}>
      <AreaChart data={chartData} margin={{ left: 0, right: 0, top: 10, bottom: 0 }}>
        <defs>
          {sortedPlatforms.map((platform) => {
            const color = PLATFORM_COLORS[platform] ?? PLATFORM_COLORS.other;
            return (
              <linearGradient key={platform} id={`gradient-${platform}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={color} stopOpacity={0.8} />
                <stop offset="95%" stopColor={color} stopOpacity={0.1} />
              </linearGradient>
            );
          })}
        </defs>
        <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.2} />
        <XAxis
          dataKey="label"
          tick={{ fill: "currentColor", fontSize: 12 }}
          stroke="currentColor"
          tickLine={false}
          axisLine={false}
          interval={granularity === "day" ? "preserveStartEnd" : 0}
        />
        <YAxis
          tickFormatter={formatAxisTick}
          tick={{ fill: "currentColor", fontSize: 12 }}
          stroke="currentColor"
          tickLine={false}
          axisLine={false}
          width={60}
        />
        <Tooltip content={<CustomTooltip />} />
        {sortedPlatforms.map((platform) => {
          const color = PLATFORM_COLORS[platform] ?? PLATFORM_COLORS.other;
          const label = PLATFORM_LABELS[platform] ?? platform;
          return (
            <Area
              key={platform}
              type="monotone"
              dataKey={platform}
              stackId="1"
              stroke={color}
              strokeWidth={1.5}
              fill={`url(#gradient-${platform})`}
              name={label}
            />
          );
        })}
      </AreaChart>
    </ResponsiveContainer>
  );
}
